import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Brain, Target, Building2, TrendingUp, Users, Rocket, Zap, ShoppingCart, MessageSquare, BarChart3, ChevronDown, Download } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const iconMap = {
  Brain: Brain,
  Target: Target,
  Building2: Building2,
  TrendingUp: TrendingUp,
  Users: Users,
  Rocket: Rocket,
  Zap: Zap,
  ShoppingCart: ShoppingCart,
  MessageSquare: MessageSquare,
  BarChart3: BarChart3
};

const Curriculum = () => {
  const [openIndex, setOpenIndex] = useState(0);
  const [sessions, setSessions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [curriculum, setCurriculum] = useState([]);

  useEffect(() => {
    fetchSessions();
  }, []);

  const fetchSessions = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/public/sessions`);
      setSessions(response.data.sessions);
      
      // Agrupar sesiones por pilar
      const grouped = groupSessionsByPilar(response.data.sessions);
      setCurriculum(grouped);
    } catch (error) {
      console.error('Error fetching sessions:', error);
      setSessions([]);
      setCurriculum([]);
    } finally {
      setIsLoading(false);
    }
  };

  const groupSessionsByPilar = (sessions) => {
    // Información completa de cada pilar
    const pilarInfo = {
      'Estrategia e Innovación Tecnológica': {
        icon: 'Target',
        summary: 'Forma pensadores estratégicos capaces de crear ventajas competitivas en entornos digitales. El estudiante aprende a detectar oportunidades, diseñar categorías, construir ventajas injustas y liderar con mentalidad exponencial.',
        competencies: 'Pensamiento 10x, diseño de categorías, innovación aplicada, growth loops, pricing estratégico y ética tecnológica'
      },
      'Emprendimiento y Negocio Digital': {
        icon: 'Rocket',
        summary: 'Entrena fundadores y empresarios digitales que crean modelos de negocio rentables desde cero. El alumno pasa de la idea al producto validado, domina métricas como LTV, CAC, Payback y diseña estructuras de rentabilidad sostenibles.',
        competencies: 'Validación de ideas, diseño de modelos de ingreso, escalamiento, capitalización y gestión legal global'
      },
      'Inteligencia Artificial Aplicada a los Negocios': {
        icon: 'Brain',
        summary: 'Desarrolla profesionales capaces de integrar IA en todas las áreas operativas de una empresa: marketing, ventas, soporte, finanzas y operaciones. El estudiante deja de ser consumidor pasivo de tecnología para convertirse en arquitecto de soluciones inteligentes.',
        competencies: 'Prompting, RAG, automatización con IA, análisis predictivo, implementación corporativa y gobierno ético de IA'
      },
      'Automatización, No-Code y Ciencia de Datos': {
        icon: 'Zap',
        summary: 'Enseña a construir sistemas, flujos y herramientas sin necesidad de programar. El alumno aprende a conectar datos, integrar APIs, crear dashboards y transformar operaciones humanas en procesos automatizados.',
        competencies: 'Arquitectura no-code, automatización empresarial, analítica de datos, dashboards y gobernanza digital'
      },
      'Marketing de Performance y Adquisición de Clientes': {
        icon: 'TrendingUp',
        summary: 'Forma estrategas de crecimiento capaces de escalar ventas con precisión matemática. Los estudiantes dominan media buying, IA en campañas, atribución multicanal y optimización de presupuesto en ecosistemas digitales.',
        competencies: 'Performance marketing, IA media buying, tracking avanzado, analítica omnicanal y escalamiento publicitario'
      },
      'Ofertas Irresistibles, Embudos y Monetización Digital': {
        icon: 'ShoppingCart',
        summary: 'Capacita para construir sistemas de venta automatizados y sostenibles. El alumno diseña ofertas irresistibles, embudos de conversión y estrategias de retención que convierten conocimiento en ingresos recurrentes.',
        competencies: 'Copywriting persuasivo, embudos automatizados, CRO, programas de fidelización y monetización avanzada'
      },
      'Ventas, Influencia y Comunicación Estratégica': {
        icon: 'MessageSquare',
        summary: 'Entrena a profesionales y fundadores para liderar desde la persuasión ética y la comunicación de alto impacto. El estudiante domina técnicas de negociación, frameworks de cierre y liderazgo comunicacional.',
        competencies: 'Neuroventas, persuasión ética, negociación avanzada, comunicación pública y liderazgo comercial'
      },
      'Marca Personal, Influencia Digital y Audiencia': {
        icon: 'Users',
        summary: 'Forma líderes de pensamiento capaces de construir autoridad en la era digital. El alumno aprende a posicionarse, crear contenido con IA, crecer orgánicamente y monetizar su influencia.',
        competencies: 'Branding estratégico, storytelling, creación de contenido con IA, PR digital y reputación de marca'
      },
      'Liderazgo, Gestión y Operaciones Empresariales': {
        icon: 'Building2',
        summary: 'Prepara directivos para dirigir empresas tecnológicas con visión, estructura y cultura sólida. El estudiante domina operaciones, finanzas, gestión de equipos, escalabilidad global y toma de decisiones bajo incertidumbre.',
        competencies: 'Liderazgo operativo, gestión de talento, cultura organizacional, dirección estratégica y productividad ejecutiva'
      },
      'Métricas, KPIs y Dirección Basada en Datos': {
        icon: 'BarChart3',
        summary: 'Desarrolla líderes "data-driven" obsesionados con la evidencia. El alumno aprende a diseñar sistemas de indicadores, interpretar datos financieros, operativos y comerciales, y dirigir con precisión matemática.',
        competencies: 'Analítica ejecutiva, dashboards integrados, forecasting, gobernanza de datos y cultura métrica institucional'
      }
    };
    
    const pilarGroups = {};
    
    sessions.forEach(session => {
      const pilarName = session.pilar || 'Sesiones Generales';
      if (!pilarGroups[pilarName]) {
        const info = pilarInfo[pilarName] || {
          icon: 'Brain',
          summary: 'Formación práctica y aplicada para dominar esta área del negocio digital.',
          competencies: 'Competencias prácticas verificables aplicables a tu negocio'
        };
        
        pilarGroups[pilarName] = {
          id: pilarName.toLowerCase().replace(/\s+/g, '-'),
          title: pilarName,
          icon: info.icon,
          summary: info.summary,
          competencies: info.competencies,
          totalSessions: 0,
          modules: []
        };
      }
      
      pilarGroups[pilarName].modules.push({
        id: session.id,
        title: session.title,
        description: session.description || 'Sesión en vivo',
        duration: session.duration || 90,
        date: session.date
      });
      pilarGroups[pilarName].totalSessions++;
    });
    
    // Generar descripción dinámica
    Object.values(pilarGroups).forEach(pilar => {
      pilar.description = `${pilar.totalSessions} sesión${pilar.totalSessions > 1 ? 'es' : ''} en vivo`;
    });
    
    return Object.values(pilarGroups);
  };

  return (
    <section id="curriculum" className="py-32 bg-gradient-to-b from-black via-gray-950 to-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-20">
          <span className="text-teal-400 text-sm uppercase tracking-widest font-semibold mb-4 block">
            El Programa
          </span>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            Urpe Liberty MBA
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto mb-12">
            Formación práctica, modular y accionable para emprendedores, profesionales y estrategas que quieren dominar la IA, la automatización y los negocios reales.
          </p>

          {/* KPIs */}
          <div className="flex flex-wrap justify-center gap-8 mb-12">
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-teal-400 mb-2">
                {isLoading ? '...' : sessions.length || '720+'}
              </div>
              <div className="text-sm text-gray-400 uppercase tracking-wide">Sesiones</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-teal-400 mb-2">
                {isLoading ? '...' : curriculum.length || '10'}
              </div>
              <div className="text-sm text-gray-400 uppercase tracking-wide">Pilares</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-teal-400 mb-2">
                {isLoading ? '...' : Math.round((sessions.reduce((sum, s) => sum + (s.duration || 90), 0)) / 60) || '1080+'}
              </div>
              <div className="text-sm text-gray-400 uppercase tracking-wide">Horas</div>
            </div>
          </div>
        </div>

        {/* Two Column Layout */}
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Left: Description */}
          <div className="lg:sticky lg:top-32 flex flex-col items-center lg:items-start">
            <p className="text-lg text-gray-300 leading-relaxed mb-8">
              Un curriculum diseñado para emprendedores que quieren construir empresas que escalan. Cada módulo combina teoría con aplicación inmediata en tu negocio.
            </p>
            <a 
              href="/LOS-10-PILARES-DE-FORMACION.pdf" 
              download="Urpe-School-10-Pilares-de-Formacion.pdf"
              className="inline-flex items-center space-x-2 px-6 py-3 bg-white/5 border border-white/10 text-white rounded-lg hover:bg-white/10 hover:border-teal-500/30 transition-all duration-300"
            >
              <Download size={18} />
              <span>Descargar Plan Académico</span>
            </a>
          </div>

          {/* Right: Accordion */}
          <div className="space-y-4">
            {isLoading && (
              <div className="flex items-center justify-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-400"></div>
              </div>
            )}
            
            {!isLoading && curriculum.length === 0 && (
              <div className="text-center py-20">
                <p className="text-gray-400 text-lg">No hay sesiones programadas en este momento.</p>
              </div>
            )}
            
            {!isLoading && curriculum.map((item, index) => {
              const Icon = iconMap[item.icon];
              const isOpen = openIndex === index;
              
              return (
                <div
                  key={item.id}
                  className="bg-gradient-to-br from-white/5 to-transparent border border-white/10 rounded-xl overflow-hidden hover:border-teal-500/30 transition-all duration-300"
                >
                  <button
                    onClick={() => setOpenIndex(isOpen ? -1 : index)}
                    className="w-full flex items-center justify-between p-6 text-left"
                  >
                    <div className="flex items-center space-x-4">
                      <div className="flex-shrink-0 w-10 h-10 bg-teal-500/10 rounded-lg flex items-center justify-center border border-teal-500/20">
                        <Icon size={20} className="text-teal-400" />
                      </div>
                      <div>
                        <span className="text-xs text-teal-400 font-mono">
                          {String(index + 1).padStart(2, '0')}
                        </span>
                        <h3 className="text-lg font-semibold text-white">
                          {item.title}
                        </h3>
                      </div>
                    </div>
                    <ChevronDown
                      size={20}
                      className={`text-gray-400 transition-transform duration-300 ${
                        isOpen ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                  
                  <div
                    className={`overflow-hidden transition-all duration-500 ${
                      isOpen ? 'max-h-[1000px]' : 'max-h-0'
                    }`}
                  >
                    <div className="px-6 pb-6 pt-0 pl-14 space-y-4">
                      {/* Resumen del pilar */}
                      <p className="text-gray-300 leading-relaxed">
                        {item.summary}
                      </p>
                      
                      {/* Competencias */}
                      <div className="pt-2 border-t border-white/10">
                        <p className="text-sm text-teal-400 font-semibold mb-2">
                          Competencias:
                        </p>
                        <p className="text-sm text-gray-400 leading-relaxed">
                          {item.competencies}
                        </p>
                      </div>
                      
                      {/* Lista de sesiones */}
                      <div className="pt-2 border-t border-white/10">
                        <p className="text-sm text-teal-400 font-semibold mb-3">
                          Sesiones incluidas ({item.totalSessions}):
                        </p>
                        <div className="space-y-2">
                          {item.modules.map((module, idx) => (
                            <div key={module.id} className="flex items-start gap-3 text-sm">
                              <span className="text-teal-400 mt-0.5">•</span>
                              <span className="text-gray-400 leading-relaxed">{module.title}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-16">
          <a
            href="#admissions"
            className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-teal-500 to-teal-600 text-black font-semibold rounded-full hover:from-teal-400 hover:to-teal-500 transition-all duration-300 shadow-2xl shadow-teal-500/30 hover:shadow-teal-500/50 hover:scale-105"
          >
            Únete a la próxima generación de emprendedores
          </a>
        </div>
      </div>
    </section>
  );
};

export default Curriculum;
